import {
  faChevronDown,
  faCheck,
  faEllipsisVertical,
  faPlus,
  faSearch,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { omit } from "lodash";
import { useCallback, useMemo, useEffect, useState } from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { Divider } from "src/base-components/Divider";
import { ErrorHint } from "src/base-components/ErrorHint";
import { FixedPositionedDropdown } from "src/base-components/FixedPositionedDropDown";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { OptionsDropdown } from "src/base-components/OptionsDropdown/OptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Pill, PillVariants } from "src/base-components/Pill";
import { Option, Select } from "src/base-components/Select";
import {
  SortableField,
  SortableFieldsContext,
} from "src/base-components/schemaKit/SortableFields";
import { CaseStatusPill } from "src/cases/common/CaseStatusPill";
import { useCaseQueues } from "src/cases/queries/queues";
import {
  CaseTemplateFormValues,
  WorkflowRuleActionWithId,
} from "src/cases/templates/caseTemplateFormUtils";
import { useAutomationCaseProperties } from "src/cases/templates/hooks";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { useIdKeyMapping } from "src/cases/templates/useIdKeyMapping";
import { useWorkflowReferences } from "src/cases/templates/useWorkflowReferences";
import {
  buildStatusKey,
  buildStatusLabel,
  FlattenedStatusValue,
  getFieldError,
} from "src/cases/templates/utils";
import { isStatusProperty } from "src/cases/utils";
import {
  CaseTemplateWorkflowRuleAction,
  CaseTemplateWorkflowRuleActionAutomation,
} from "src/clients/case-management/api";
import { EmptyState } from "src/design-system/EmptyState";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { CaseTemplateVersionsPageParams } from "src/router/urls";
import { logger } from "src/utils/logger";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const CaseWorkflowEditor: React.FC<{
  statusValue: FlattenedStatusValue;
  statusValues: FlattenedStatusValue[];
  ensureWorkflowRules: (statusValue: FlattenedStatusValue) => void;
}> = ({ statusValue, statusValues, ensureWorkflowRules }) => {
  const { control, setValue } = useFormContext<CaseTemplateFormValues>();
  const hasPermission = useCheckFieldPermission();
  const { createWorkflowAction } = useIdKeyMapping();

  const statusKey = statusValue._statusKey;

  const {
    fields: workflows,
    append,
    remove,
    move,
  } = useFieldArray<
    CaseTemplateFormValues,
    `attributes.workflow_rules.${string}.actions`,
    "_id"
  >({
    name: `attributes.workflow_rules.${statusKey}.actions`,
    keyName: "_id",
  });

  const caseActions = useWatch<
    CaseTemplateFormValues,
    "attributes.case_actions"
  >({
    name: "attributes.case_actions",
  });

  const isAddWorkflowDisabled = useMemo(() => {
    const validActionsCount =
      caseActions?.filter((action) => !!action.key?.length).length ?? 0;
    return workflows.length >= validActionsCount;
  }, [caseActions, workflows]);

  return (
    <div className="flex flex-col gap-y-3" data-loc="workflow-rules-editor">
      {workflows.length ? (
        <>
          <SortableFieldsContext
            items={workflows.map((workflow) => workflow._id)}
            onChangeOrder={({ oldIndex, newIndex }) => {
              move(oldIndex, newIndex);
            }}
          >
            {workflows.map((workflow, index) => (
              <Card key={workflow._id} dataLoc={`workflow-rules-${index}`}>
                <SortableField id={workflow._id}>
                  <WorkflowActionFrame
                    actionError={
                      <EditorFieldErrorHint
                        keyPath={`attributes.workflow_rules.${statusKey}.actions.${index}.action_key`}
                      />
                    }
                    deleteWorkflow={
                      <div data-loc={`delete-workflow-${index}`}>
                        <Icon
                          color="text-gray-500 hover:text-gray-800"
                          dataLoc={`delete-workflow-${index}-button`}
                          icon={faTrashCan}
                          size="sm"
                          onClick={() => remove(index)}
                        />
                      </div>
                    }
                    requiredTasksSelect={
                      <ActionRequiredTasksSelect
                        actionIndex={index}
                        statusKey={statusKey}
                      />
                    }
                    selectAction={
                      <Controller
                        control={control}
                        name={`attributes.workflow_rules.${statusKey}.actions.${index}.action_key`}
                        render={({ field }) => (
                          <CaseActionsSelect
                            actionIndex={index}
                            disabled={
                              !hasPermission(
                                "edit",
                                `attributes.workflow_rules.${statusKey}.actions.${index}.action_key`,
                              )
                            }
                            selected={field.value}
                            statusKey={statusKey}
                            onSelect={(actionKey) => {
                              const workflowAction =
                                createWorkflowAction(actionKey);
                              field.onChange(workflowAction.action_key);
                              // Also update the _internalId field
                              const internalIdPath =
                                `attributes.workflow_rules.${statusKey}.actions.${index}._internalId` as const;
                              setValue(
                                internalIdPath,
                                workflowAction._internalId,
                              );
                            }}
                          />
                        )}
                        rules={{
                          required: "Please select an action",
                        }}
                      />
                    }
                    workflowAutomationEditor={
                      <WorkflowAutomationEditor
                        actionIndex={index}
                        statusValue={statusValue}
                      />
                    }
                    workflowMenu={
                      <DuplicateWorkflowMenu
                        statusValue={statusValue}
                        statusValues={statusValues}
                        workflowIndex={index}
                        onAddStatus={ensureWorkflowRules}
                      />
                    }
                  />
                </SortableField>
              </Card>
            ))}
          </SortableFieldsContext>

          {hasPermission("add", "attributes.workflow_rules") && (
            <div>
              <Button
                dataLoc="add-workflow-button"
                disabled={isAddWorkflowDisabled}
                iconLeft={faPlus}
                size="sm"
                variant="secondary"
                onClick={() => append(createWorkflowAction(""))}
              >
                Add workflow
              </Button>
            </div>
          )}
        </>
      ) : (
        <EmptyState
          actionButton={{
            text: "Add workflow",
            icon: faPlus,
            disabled:
              isAddWorkflowDisabled ||
              !hasPermission("add", "attributes.workflow_rules"),
            onClick: () => {
              ensureWorkflowRules(statusValue);
              append(createWorkflowAction(""));
            },
          }}
          dataLoc="empty-workflow-rules"
          description="Configure workflow rules to automate what happens when an action is triggered."
          headline="No workflow rules configured yet"
          icon={faSearch}
        />
      )}
    </div>
  );
};

const CaseActionsSelect: React.FC<{
  selected?: string;
  disabled?: boolean;
  statusKey: string;
  actionIndex: number;
  onSelect: (value: string) => void;
}> = ({ selected, disabled, statusKey, actionIndex, onSelect }) => {
  const { isSameAction } = useIdKeyMapping();
  const actions = useWatch<CaseTemplateFormValues, "attributes.case_actions">({
    name: "attributes.case_actions",
  });

  const allWorkflowActions = useWatch<
    CaseTemplateFormValues,
    `attributes.workflow_rules.${string}.actions`
  >({
    name: `attributes.workflow_rules.${statusKey}.actions`,
  });

  const actionOptions = useMemo(() => {
    if (!actions) return [];

    const isActionAvailable = (actionKey: string) => {
      if (!actionKey) return false;
      if (actionKey === selected) return true;

      const action = actions.find((a) => a.key === actionKey);
      if (!action?._internalId) return false;

      const candidateAction = {
        action_key: actionKey,
        automation: [],
        _internalId: action._internalId,
      };

      return !allWorkflowActions?.some((workflow, idx) => {
        if (idx === actionIndex) return false; // Skip self
        return isSameAction(workflow, candidateAction);
      });
    };

    return actions
      .filter((action) => !!action.key?.length && isActionAvailable(action.key))
      .map((action) => ({
        key: action.key,
        value: (
          <Pill size="sm" variant="gray" maxWidth>
            <Pill.Text>{action.display_name}</Pill.Text>
          </Pill>
        ),
      }));
  }, [actions, selected, allWorkflowActions, actionIndex, isSameAction]);

  return (
    <Select
      dataLoc="action-select"
      disabled={disabled || !actionOptions.length}
      options={actionOptions}
      placeholder="Select an action"
      value={selected}
      optionsFitContent
      onChange={onSelect}
    />
  );
};

const ActionRequiredTasksSelect: React.FC<{
  statusKey: string;
  actionIndex: number;
}> = ({ statusKey, actionIndex }) => {
  const { control } = useFormContext<CaseTemplateFormValues>();
  const { removeTaskFromSpecificStatus } = useWorkflowReferences();
  const { createRequiredTask } = useIdKeyMapping();
  const hasPermission = useCheckFieldPermission();

  const statusTasks = useWatch<
    CaseTemplateFormValues,
    `attributes.workflow_rules.${string}.workflow_tasks_keys`
  >({
    name: `attributes.workflow_rules.${statusKey}.workflow_tasks_keys`,
  });

  const allTasks = useWatch<CaseTemplateFormValues, "attributes.case_tasks">({
    name: "attributes.case_tasks",
  });

  const currentRequiredTasks = useWatch({
    name: `attributes.workflow_rules.${statusKey}.actions.${actionIndex}.required_tasks_keys`,
  });

  const taskOptions = useMemo(() => {
    if (!statusTasks || !allTasks) return [];

    const statusTaskKeys = statusTasks
      .map((task) => task.task_key)
      .filter(Boolean);

    return allTasks
      .filter((task) => statusTaskKeys.includes(task.key))
      .map((task) => ({
        key: task.key,
        value: (
          <Pill size="sm" variant="gray" maxWidth>
            <Pill.Text>{task.display_name}</Pill.Text>
          </Pill>
        ),
        displayValue: task.display_name,
      }));
  }, [statusTasks, allTasks]);

  // Clean up invalid task references when workflow_tasks_keys changes
  useEffect(() => {
    if (!currentRequiredTasks || !statusTasks) return;

    const statusTaskIds = statusTasks
      .map((task) => task._internalId)
      .filter(Boolean);
    const invalidTasks = currentRequiredTasks.filter(
      (task: { _internalId: string }) =>
        !statusTaskIds.includes(task._internalId),
    );

    invalidTasks.forEach((task: { _internalId: string }) => {
      removeTaskFromSpecificStatus(task._internalId, statusKey);
    });
  }, [
    statusTasks,
    currentRequiredTasks,
    removeTaskFromSpecificStatus,
    statusKey,
  ]);

  return (
    <Controller
      control={control}
      name={`attributes.workflow_rules.${statusKey}.actions.${actionIndex}.required_tasks_keys`}
      render={({ field }) => (
        <Select
          dataLoc={`action-required-tasks-${actionIndex}`}
          disabled={
            !hasPermission(
              "edit",
              `attributes.workflow_rules.${statusKey}.actions.${actionIndex}.required_tasks_keys`,
            ) || taskOptions.length === 0
          }
          options={taskOptions}
          placeholder={
            taskOptions.length === 0
              ? "No tasks configured for this status"
              : "Select task(s)"
          }
          value={field.value?.map((task) => task.task_key) ?? []}
          multiple
          optionsFitContent
          onChange={(selectedKeys) => {
            const selectedTasks = (selectedKeys as string[]).map((taskKey) =>
              createRequiredTask(taskKey),
            );
            field.onChange(selectedTasks);
          }}
        />
      )}
    />
  );
};

const WorkflowAutomationEditor: React.FC<{
  statusValue: FlattenedStatusValue;
  actionIndex: number;
}> = ({ statusValue, actionIndex }) => {
  const { templateId } = useParamsDecode<CaseTemplateVersionsPageParams>();
  const { getValues, control } = useFormContext<CaseTemplateFormValues>();
  const properties = useAutomationCaseProperties();

  const { data: caseQueues, isLoading: isLoadingCaseQueues } = useCaseQueues();

  const statusKey = statusValue._statusKey;

  const {
    fields: automations,
    move,
    remove,
    append,
  } = useFieldArray<
    CaseTemplateFormValues,
    `attributes.workflow_rules.${string}.actions.${number}.automation`,
    "_id"
  >({
    name: `attributes.workflow_rules.${statusKey}.actions.${actionIndex}.automation`,
    keyName: "_id",
  });

  // Helper to check if a field is already used in the current automations
  const isExistingField = useCallback(
    (field: string) =>
      automations.some((automation) => automation.field === field),
    [automations],
  );

  const automationOptions = useMemo(
    () =>
      [
        {
          key: getAutomationFieldLabel("status"),
          action: () =>
            append({
              field: "status",
              value: "",
            }),
          disabled: isExistingField("status"),
        },
        {
          key: getAutomationFieldLabel("queue_id"),
          action: () =>
            append({
              field: "queue_id",
              value: "",
            }),
          disabled: isExistingField("queue_id"),
        },
        ...properties
          // Exclude status property as it's handled separately
          .filter((property) => !isStatusProperty(property))
          .map((property) => ({
            key: getAutomationFieldLabel(property.key, property.display_name),
            action: () => {
              append({
                field: property.key,
                value: property.type === "tags" ? [] : "",
              });
            },
            disabled: isExistingField(property.key),
          })),
      ] satisfies OptionsDropdownElement[],
    [append, properties, isExistingField],
  );

  const caseQueueOptions = useMemo(
    () =>
      caseQueues
        ?.filter((queue) =>
          queue.template_configs.some(
            (config) => config.template.id === templateId,
          ),
        )
        .map((queue) => ({
          value: (
            <Pill size="sm" variant="gray" maxWidth>
              <Pill.Text>{queue.name}</Pill.Text>
            </Pill>
          ),
          key: queue.id,
        })) ?? [],
    [caseQueues, templateId],
  );

  const statusOptions = useMemo(() => {
    const statusProperty = properties.find(
      (property) => property.key === "status" && property.type === "enum",
    );

    return (
      statusProperty?.values?.flatMap((status) => {
        if ("sub_values" in status && status.sub_values?.length) {
          return status.sub_values.map((subValue) => ({
            key: buildStatusKey(status._internalId, subValue._internalId),
            value: (
              <CaseStatusPill
                status={status.value}
                subStatus={subValue.value}
                variant={(status.color as PillVariants) ?? "gray"}
              />
            ),
          }));
        }

        return [
          {
            key: buildStatusKey(status._internalId),
            value: (
              <Pill
                size="sm"
                variant={(status.color as PillVariants) ?? "gray"}
              >
                <Pill.Text>{status.value}</Pill.Text>
              </Pill>
            ),
          },
        ];
      }) ?? []
    );
  }, [properties]);

  // Get props for the property select component
  const getPropertySelectProps = useCallback(
    (field: string) => {
      const property = properties.find((property) => property.key === field);
      const isTags = property?.type === "tags";

      const options =
        property?.values?.map((value) => ({
          key: value.value,
          value: isTags ? (
            value.value
          ) : (
            <Pill
              size="sm"
              variant={(value.color as PillVariants) ?? "gray"}
              maxWidth
            >
              <Pill.Text>{value.value}</Pill.Text>
            </Pill>
          ),
        })) ?? [];

      return {
        options,
        displayName: property?.display_name,
        multiple: isTags,
      };
    },
    [properties],
  );

  const canAddMoreAutomations = useMemo(() => {
    const automatedFields = automations.map((automation) => automation.field);
    const allFields = [
      "status",
      "queue_id",
      ...properties.map((property) => property.key),
    ];

    return allFields.some((field) => !automatedFields.includes(field));
  }, [automations, properties]);

  return (
    <div className="max-w-full">
      <Label>
        <span className="font-inter-semibold-13px">
          Configure the automation triggered by this action
        </span>
      </Label>

      <SortableFieldsContext
        items={automations.map((automation) => automation._id)}
        onChangeOrder={({ oldIndex, newIndex }) => {
          move(oldIndex, newIndex);
        }}
      >
        <div className="flex flex-col gap-y-2">
          {automations.map((automation, index) => {
            // Prevent modifying sub_status directly
            // it should be added/removed automatically when status is changed
            if (automation.field === "sub_status") return null;

            const property = properties.find(
              (prop) => prop.key === automation.field,
            );

            return (
              <SortableField key={automation._id} id={automation._id}>
                <div data-loc={`automation-${automation.field}`}>
                  <WorkflowAutomationFrame
                    deleteAutomation={
                      <Icon
                        color="text-gray-500 hover:text-gray-800"
                        icon={faTrashCan}
                        size="sm"
                        onClick={() => {
                          // If removing status field, also remove sub_status if present
                          remove(index);

                          if (automation.field === "status") {
                            const subStatusFieldIndex = automations.findIndex(
                              isSubStatusAutomation,
                            );

                            if (subStatusFieldIndex > -1) {
                              remove(subStatusFieldIndex);
                            }
                          }
                        }}
                      />
                    }
                    fieldInput={
                      <Input
                        value={getAutomationFieldLabel(
                          automation.field,
                          property?.display_name,
                        )}
                        disabled
                        fullWidth
                      />
                    }
                    selectValue={
                      <Controller
                        control={control}
                        name={`attributes.workflow_rules.${statusKey}.actions.${actionIndex}.automation.${index}.value`}
                        render={({ field: valueField }) => {
                          const field = getValues(
                            `attributes.workflow_rules.${statusKey}.actions.${actionIndex}.automation.${index}.field`,
                          );

                          if (field === "status") {
                            return (
                              <StatusSelect
                                options={statusOptions}
                                value={String(valueField.value)}
                                onSelect={valueField.onChange}
                              />
                            );
                          }

                          if (field === "queue_id") {
                            return (
                              <Select
                                disabled={!caseQueueOptions.length}
                                loading={isLoadingCaseQueues}
                                options={caseQueueOptions}
                                placeholder="Select a case queue"
                                value={String(valueField.value)}
                                optionsFitContent
                                onChange={valueField.onChange}
                              />
                            );
                          }

                          const {
                            options: propertyOptions,
                            multiple,
                            displayName,
                          } = getPropertySelectProps(field);

                          if (multiple) {
                            return (
                              <Select
                                disabled={!propertyOptions.length}
                                options={propertyOptions}
                                placeholder={`Select ${displayName?.toLowerCase() ?? "values"}`}
                                value={
                                  Array.isArray(valueField.value)
                                    ? valueField.value
                                    : []
                                }
                                multiple
                                optionsFitContent
                                onChange={valueField.onChange}
                              />
                            );
                          }

                          return (
                            <Select
                              options={propertyOptions}
                              placeholder={`Select ${displayName?.toLowerCase() ?? "a value"}`}
                              value={String(valueField.value)}
                              optionsFitContent
                              onChange={valueField.onChange}
                            />
                          );
                        }}
                        rules={{
                          required: "Please select a value",
                        }}
                      />
                    }
                    valueError={
                      <EditorFieldErrorHint
                        keyPath={`attributes.workflow_rules.${statusKey}.actions.${actionIndex}.automation.${index}.value`}
                      />
                    }
                  />
                </div>
              </SortableField>
            );
          })}
        </div>
      </SortableFieldsContext>

      <div className="mt-3 flex items-start">
        <OptionsDropdown
          buttonDataLoc="add-automation-button"
          disabled={!canAddMoreAutomations}
          elements={automationOptions}
          placement="bottom start"
        >
          <Button
            disabled={!canAddMoreAutomations}
            iconLeft={faPlus}
            iconRight={faChevronDown}
            size="sm"
            variant="secondary"
          >
            Add automation
          </Button>
        </OptionsDropdown>
      </div>
    </div>
  );
};

const StatusSelect: React.FC<{
  value: string | null;
  options: Option[];
  onSelect: (value: string) => void;
}> = ({ value, options, onSelect }) => {
  return (
    <Select
      dataLoc="automation-status-selector"
      disabled={!options.length}
      options={options}
      placeholder="Select status"
      value={value}
      optionsFitContent
      onChange={onSelect}
    />
  );
};

const DuplicateWorkflowMenu: React.FC<{
  workflowIndex: number;
  statusValue: FlattenedStatusValue;
  onAddStatus: (statusValue: FlattenedStatusValue) => void;
  statusValues: FlattenedStatusValue[];
}> = ({ statusValues, workflowIndex, statusValue, onAddStatus }) => {
  const statusKey = statusValue._statusKey;

  const workflowToDuplicate = useWatch<
    CaseTemplateFormValues,
    `attributes.workflow_rules.${string}.actions.${number}`
  >({
    name: `attributes.workflow_rules.${statusKey}.actions.${workflowIndex}`,
  });

  const { getValues, setValue } = useFormContext<CaseTemplateFormValues>();
  const { createWorkflowAction } = useIdKeyMapping();

  const allWorkflowRules = useWatch<
    CaseTemplateFormValues,
    "attributes.workflow_rules"
  >({
    name: "attributes.workflow_rules",
  });

  const [selectedStatusKeys, setSelectedStatusKeys] = useState<string[]>([]);

  useEffect(() => {
    setSelectedStatusKeys([]);
  }, [workflowIndex, workflowToDuplicate?.action_key]);

  const hasWorkflowInStatus = useCallback(
    (statusKey: string, actionKey: string) => {
      // Use getValues() for real-time form state to avoid stale state issues
      // when the menu reopens after duplicating workflows
      const workflowRules = getValues(`attributes.workflow_rules.${statusKey}`);

      return workflowRules?.actions?.some(
        (action) => action.action_key === actionKey,
      );
    },
    [getValues],
  );

  const duplicateWorkflow = useCallback(
    (workflow: CaseTemplateWorkflowRuleAction, toStatusKey: string) => {
      const workflowActions = getValues(
        `attributes.workflow_rules.${toStatusKey}.actions`,
      );

      const actionWithInternalId = createWorkflowAction(workflow.action_key);

      setValue(`attributes.workflow_rules.${toStatusKey}.actions`, [
        ...workflowActions,
        {
          ...omit(structuredClone(workflow), "_id"),
          _internalId: actionWithInternalId._internalId,
        } as WorkflowRuleActionWithId,
      ]);
    },
    [setValue, getValues, createWorkflowAction],
  );

  const handleSelectionChange = useCallback(
    (newSelectedKeys: string[]) => {
      try {
        if (!workflowToDuplicate?.action_key) {
          return;
        }

        let duplicatedCount = 0;
        newSelectedKeys.forEach((statusLabel) => {
          const targetStatusValue = statusValues.find(
            (sv) => buildStatusLabel(sv) === statusLabel,
          );

          if (targetStatusValue) {
            const targetStatusKey = targetStatusValue._statusKey;
            if (
              workflowToDuplicate &&
              !hasWorkflowInStatus(
                targetStatusKey,
                workflowToDuplicate.action_key,
              )
            ) {
              onAddStatus(targetStatusValue);
              duplicateWorkflow(workflowToDuplicate, targetStatusKey);
              duplicatedCount++;
            }
          }
        });

        setSelectedStatusKeys(newSelectedKeys);

        if (duplicatedCount > 0) {
          toastActions.success({
            title: `Workflow successfully duplicated`,
          });
        }
      } catch (error) {
        logger.error("Error duplicating workflow:", error);
        toastActions.failure({
          title: "Failed to duplicate workflow",
          description:
            "An error occurred while duplicating the workflow. Please try again later.",
        });
      }
    },
    [
      workflowToDuplicate,
      statusValues,
      duplicateWorkflow,
      onAddStatus,
      hasWorkflowInStatus,
    ],
  );

  const buildDuplicateToOptions = useMemo(
    () =>
      statusValues.map((statusValue) => {
        const statusLabel = buildStatusLabel(statusValue);
        const alreadyHasWorkflow = workflowToDuplicate
          ? hasWorkflowInStatus(
              statusValue._statusKey,
              workflowToDuplicate.action_key,
            )
          : false;

        return {
          key: statusLabel,
          value: statusLabel,
          disabled: alreadyHasWorkflow,
        };
      }),
    // Add allWorkflowRules to trigger recomputation when workflows change
    // (hasWorkflowInStatus uses getValues() for real-time checks, but we need
    // this dependency to trigger the useMemo when form state updates)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusValues, hasWorkflowInStatus, workflowToDuplicate, allWorkflowRules],
  );

  const renderButtonValue = () => (
    <Icon
      color={
        !workflowToDuplicate?.action_key?.length
          ? "text-gray-300"
          : "text-gray-500 hover:text-gray-700"
      }
      disabled={!workflowToDuplicate?.action_key?.length}
      icon={faEllipsisVertical}
      size="xs"
    />
  );

  const renderValue = ({
    value,
    selected,
    disabled,
  }: {
    key: string;
    value: string;
    selected: boolean;
    disabled?: boolean;
  }) => (
    <div className="flex min-w-0 items-center justify-between gap-x-2">
      <div
        className={twJoin(
          "min-w-0 truncate",
          (selected || disabled) && "text-gray-400",
        )}
      >
        {value}
      </div>
      {selected && <Icon color="text-indigo-500" icon={faCheck} size="sm" />}
    </div>
  );

  const renderHeader = () => (
    <div className="px-4 py-2.5 text-gray-800 font-inter-semibold-13px">
      Duplicate to:
    </div>
  );

  return (
    <Tooltip placement="bottom" title="Duplicate workflow" asChild>
      {/* MenuButton in FixedPositionedDropdown cannot be a child of Tooltip */}
      <div>
        <FixedPositionedDropdown
          buttonClassName="border-0 bg-transparent ring-0 focus:ring-0 focus:outline-none hover:text-gray-700 outline-none transition-opacity duration-200"
          className="inline-block"
          dataLoc={`workflow-menu-${workflowIndex}`}
          disabled={!workflowToDuplicate?.action_key?.length}
          elements={buildDuplicateToOptions}
          placement="bottom end"
          renderButtonValue={renderButtonValue}
          renderHeader={renderHeader}
          renderValue={renderValue}
          selected={selectedStatusKeys}
          multiple
          onSelect={(keys) => handleSelectionChange(keys as string[])}
        />
      </div>
    </Tooltip>
  );
};

const WorkflowActionFrame: React.FC<{
  selectAction: React.ReactNode;
  deleteWorkflow: React.ReactNode;
  actionError: React.ReactNode;
  workflowAutomationEditor: React.ReactNode;
  workflowMenu: React.ReactNode;
  requiredTasksSelect: React.ReactNode;
}> = ({
  selectAction,
  deleteWorkflow,
  actionError,
  workflowMenu,
  workflowAutomationEditor,
  requiredTasksSelect,
}) => {
  return (
    <div className="flex flex-col gap-y-3">
      <div className="flex flex-col gap-y-4">
        <div>
          <div className="flex items-center gap-x-2">
            <div className="flex-1">{selectAction}</div>
            {workflowMenu}
            {deleteWorkflow}
          </div>
          {actionError}
        </div>
        {workflowAutomationEditor}
      </div>
      <Divider />
      <div>
        <Label>
          Configure which tasks need to be completed for this action
        </Label>
        <div className="min-w-0 max-w-full">{requiredTasksSelect}</div>
      </div>
    </div>
  );
};

const WorkflowAutomationFrame: React.FC<{
  fieldInput: React.ReactNode;
  selectValue: React.ReactNode;
  deleteAutomation: React.ReactNode;
  valueError: React.ReactNode;
}> = ({ fieldInput, selectValue, deleteAutomation, valueError }) => (
  <div>
    <div className="flex min-w-0 max-w-full items-start gap-x-2">
      <div className="flex min-w-0 flex-1 items-start gap-2">
        <div className="min-w-0 max-w-full flex-1">{fieldInput}</div>
        <div className="min-w-0 max-w-full flex-1">{selectValue}</div>
      </div>
      <div className="flex items-start pt-1">{deleteAutomation}</div>
    </div>
    {valueError}
  </div>
);

const EditorFieldErrorHint: React.FC<{
  keyPath: string;
}> = ({ keyPath }) => {
  const form = useFormContext<CaseTemplateFormValues>();
  const error = getFieldError(form, keyPath);
  if (!error) return null;
  return <ErrorHint>{error.message}</ErrorHint>;
};

const isSubStatusAutomation = (
  automation: CaseTemplateWorkflowRuleActionAutomation,
) => automation.field === "sub_status";

const getAutomationFieldLabel = (field: string, displayName?: string) => {
  if (field === "status") return "Change status to";
  if (field === "queue_id") return "Change queue to";
  return `Set ${displayName?.toLowerCase() ?? "value"} to`;
};
